import { Fragment, type ReactNode } from 'react';
import { useApp, hueOf } from '../state';
import { useFeedback } from './feedback';
import { api, enc } from '../api';
import { fmtDate, fmtWhen } from '../format';
import { Area, DateField, Grid } from './fields';

export const Mono = ({ children }: { children: ReactNode }) => <span className="mono">{children}</span>;
export const Ref = ({ to, children }: { to: string; children: ReactNode }) => <a href={to} className="mono">{children}</a>;
export const Sub = ({ children }: { children: ReactNode }) => <span className="sub">{children}</span>;
export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;
export const Loading = () => <p className="loading">Loading…</p>;

/** MID/TID with muted separators: SI.EML.PSPCT.26.001 */
export const MidDisplay = ({ id }: { id: string }) => (
  <div className="mid-display"><span className="mono">{id.split('.').map((s, i) => <Fragment key={i}>{i > 0 && <span className="sep">.</span>}{s}</Fragment>)}</span></div>
);

export function Pill({ code }: { code: number }) {
  const { lifecycleOf, statusName } = useApp();
  return <span className={`pill lc-${lifecycleOf(code)}`}>{statusName(code)} <code>{code}</code></span>;
}

export type Move = { to_code: number; to_name: string; label?: string; allowed: boolean; requires_role: string; is_default?: number };

/** The status ladder: every status, passed/current/legal-next marked. The one bold element on a detail page. */
export function Ladder({ current, moves = [] }: { current: number; moves?: Move[] }) {
  const { vocab } = useApp();
  const legal = new Set(moves.map((m) => Number(m.to_code)));
  const codes = [...vocab.status_code].sort((a, b) => a.code - b.code);
  return (
    <div className="ladder"><ol>
      {codes.map((s) => (
        <li key={s.code} title={s.description || s.name}
          className={['lc-' + s.lifecycle, s.code < current ? 'passed' : '', s.code === Number(current) ? 'current' : '', legal.has(s.code) ? 'legal' : ''].filter(Boolean).join(' ')}>
          <code>{s.code}</code><span className="name">{s.name}</span>
        </li>
      ))}
    </ol></div>
  );
}

export function MoveButtons({ moves, onMove }: { moves: Move[]; onMove: (to: number) => void }) {
  const { lifecycleOf } = useApp();
  if (!moves?.length) return <div className="moves"><span className="why">Nothing follows this status.</span></div>;
  const blocked = moves.filter((m) => !m.allowed);
  return (
    <div className="moves">
      {moves.filter((m) => m.allowed).map((m) => <button key={m.to_code} className={`hue-${hueOf(lifecycleOf(m.to_code))}`} onClick={() => onMove(m.to_code)}>{m.label || m.to_name}</button>)}
      {blocked.length > 0 && <span className="why">{blocked.map((m) => `${m.label || m.to_name} needs ${m.requires_role}`).join(' · ')}</span>}
    </div>
  );
}

/** Open the move dialog for a campaign or track. Each move is a ledger row; the database refuses illegal ones. */
export function useMoveDialog() {
  const { openDialog, toast } = useFeedback();
  const { lifecycleOf, statusName } = useApp();
  return (kind: 'campaign' | 'track', ref: string, current: number, moves: Move[], onDone: () => void, preselect?: number) => {
    const def = preselect ?? (moves.find((x) => x.allowed && x.is_default) || moves.find((x) => x.allowed))?.to_code;
    openDialog({
      title: `Move ${ref}`,
      okLabel: 'Record the move',
      body: (<>
        <p className="muted small">Now at {statusName(current)} <code>{current}</code>. Each move is a ledger row; the database refuses anything the transition table does not allow.</p>
        <div className="move-options">
          {moves.map((m) => (
            <label key={m.to_code} className={m.allowed ? '' : 'blocked'}>
              <input type="radio" name="to_status" value={m.to_code} disabled={!m.allowed} defaultChecked={Number(m.to_code) === Number(def)} />
              <span className={`dot lc-${lifecycleOf(m.to_code)}`} />
              <span>{m.label || m.to_name} <span className="muted">→ {m.to_name} <code>{m.to_code}</code></span></span>
              {!m.allowed && <span className="role">needs {m.requires_role}</span>}
            </label>
          ))}
        </div>
        <Grid><DateField name="planned_exit_at" label="This step should be done by" /><DateField name="entered_at" label="Entered on (blank = now)" /></Grid>
        <Area name="note" label="Note (why, or what happened)" />
      </>),
      onSubmit: async (v) => {
        if (!v.to_status) throw new Error('Choose a move');
        const path = kind === 'campaign' ? `/campaigns/${enc(ref)}/moves` : `/tracks/${enc(ref)}/moves`;
        const r = await api('POST', path, { to_status: Number(v.to_status), planned_exit_at: v.planned_exit_at || null, entered_at: v.entered_at || null, note: v.note || null });
        toast(`${ref} → ${r.status_name}`); onDone();
      },
    });
  };
}

export type LedgerRow = { start_date: string; exited_at?: string; end_date?: string; actor?: string; tid?: string; mid?: string; last_status_name?: string; next_status_name: string; next_status: number; note?: string };
export function LedgerList({ rows, withRef = false }: { rows: LedgerRow[]; withRef?: boolean }) {
  if (!rows.length) return <Empty>No steps recorded yet.</Empty>;
  return (
    <ul className="ledger">
      {rows.map((r, i) => (
        <li key={i} className={r.exited_at ? '' : 'open'}>
          <div className="when">{fmtWhen(r.start_date)}{r.exited_at ? ` → ${fmtWhen(r.exited_at)}` : r.end_date ? ` · due ${fmtDate(r.end_date)}` : ' · open'}{r.actor ? ` · ${r.actor}` : ''}</div>
          <div className="what">
            {withRef && <><Ref to={`#/${r.tid ? 'tracks/' + r.tid : 'campaigns/' + r.mid}`}>{r.tid || r.mid}</Ref>{' '}</>}
            {r.last_status_name ? `${r.last_status_name} → ` : ''}{r.next_status_name} <code>{r.next_status}</code>
          </div>
          {r.note && <div className="note">{r.note}</div>}
        </li>
      ))}
    </ul>
  );
}

export function Notes({ notes, entityType, entityId, onAdded }: { notes: any[]; entityType: string; entityId: string; onAdded: () => void }) {
  const { can } = useApp();
  const { toast } = useFeedback();
  const add = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const f = ev.currentTarget;
    const fd = new FormData(f);
    try { await api('POST', '/notes', { entity_type: entityType, entity_id: entityId, body: fd.get('body'), is_pinned: fd.get('is_pinned') ? 1 : 0 }); f.reset(); toast('Note added'); onAdded(); }
    catch (err) { toast((err as Error).message, true); }
  };
  return (
    <section className="notes"><h3>Notes</h3>
      {notes.length ? notes.map((n) => (
        <div key={n.id} className="note-item"><div className="meta">{n.author || 'Someone'} · {fmtWhen(n.created_at)}{n.is_pinned ? ' · pinned' : ''}</div>{n.body}</div>
      )) : <p className="muted small">No notes yet.</p>}
      {can('contributor') && (
        <form className="note-form" onSubmit={add}>
          <textarea name="body" placeholder="Add a note" required />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <button className="quiet" type="submit">Add note</button>
            <label className="check"><input type="checkbox" name="is_pinned" /> pin</label>
          </div>
        </form>
      )}
    </section>
  );
}

export const Facts = ({ items }: { items: Array<[string, ReactNode] | false | null | undefined> }) => (
  <dl className="facts">{items.filter(Boolean).map((it) => { const [k, v] = it as [string, ReactNode]; return <div key={k}><dt>{k}</dt><dd>{v}</dd></div>; })}</dl>
);

export function PageHead({ title, sub, pre, actions }: { title: ReactNode; sub?: ReactNode; pre?: ReactNode; actions?: ReactNode }) {
  return <div className="page-head"><div>{pre && <div className="pre chips">{pre}</div>}<h1>{title}</h1>{sub && <div className="sub">{sub}</div>}</div>{actions && <div className="chips">{actions}</div>}</div>;
}

export function Tabs<T extends string>({ items, current, onPick }: { items: Array<[T, string]>; current: T; onPick: (t: T) => void }) {
  return <div className="tabs" role="tablist">{items.map(([k, label]) => <button key={k} role="tab" aria-selected={k === current} onClick={() => onPick(k)}>{label}</button>)}</div>;
}
